import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Send, Paperclip, CheckCircle, Clock, HeadphonesIcon,
  Mail, Phone, ShoppingBag, MessageSquare, User, XCircle, BadgeCheck, Loader
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContextMySQL';
import { authHeaders, API_URL } from '../api';
import CoinLoader from '../components/CoinLoader';
import io from 'socket.io-client';
import { mergeMessages, isNearBottom, scrollChatToEnd, formatTime, formatDate } from '../components/chat/chatShared';
import ConfirmModal from '../components/chat/ConfirmModal';
import { EmojiButton, Lightbox, Attachment, NewMessagesPill, SendStatus } from '../components/chat/ChatParts';

const SOCKET_URL = API_URL;
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf'];
const MAX_FILE_MB = 10;

const STATUS_LABELS = {
  pending: 'En attente', active: 'Approuvée', paid: 'Payée',
  completed: 'Terminée', cancelled: 'Annulée', disputed: 'Litige',
};

// Actions admin possibles selon le statut courant
const ORDER_ACTIONS = (status) => {
  if (status === 'cancelled' || status === 'completed') return [];
  const actions = [];
  if (status === 'pending' || status === 'disputed') actions.push({ to: 'active', label: 'Approuver', cls: 'bg-green-500/20 text-green-400 border-green-500/40 hover:bg-green-500/30' });
  if (status === 'active' || status === 'pending') actions.push({ to: 'paid', label: 'Marquer payée', cls: 'bg-blue-500/20 text-blue-400 border-blue-500/40 hover:bg-blue-500/30' });
  if (status === 'paid' || status === 'active') actions.push({ to: 'completed', label: 'Terminer', cls: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40 hover:bg-yellow-500/30' });
  actions.push({ to: 'cancelled', label: 'Annuler', danger: true, cls: 'bg-red-500/20 text-red-400 border-red-500/40 hover:bg-red-500/30' });
  return actions;
};

export default function AdminChat() {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const { user, isAuthenticated, isAdmin, logout, loading: authLoading } = useAuth();
  const chatRef = useRef(null);
  const fileInputRef = useRef(null);
  const nearBottomRef = useRef(true);
  const autoScrollRef = useRef(false);
  const sentByMe = useRef(new Set());

  const [connected, setConnected] = useState(false);
  const [usingLocalStorage, setUsingLocalStorage] = useState(false);
  const [chatData, setChatData] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [newBelowCount, setNewBelowCount] = useState(0);
  const [confirmAction, setConfirmAction] = useState(null); // { to, label }
  const [statusBusy, setStatusBusy] = useState(false);

  const handleScroll = () => {
    const el = chatRef.current;
    if (!el) return;
    const near = isNearBottom(el);
    if (autoScrollRef.current) {
      if (near) autoScrollRef.current = false;
      return;
    }
    nearBottomRef.current = near;
    if (near) setNewBelowCount(0);
  };

  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    const cancel = () => { autoScrollRef.current = false; };
    el.addEventListener('wheel', cancel, { passive: true });
    el.addEventListener('touchstart', cancel, { passive: true });
    return () => {
      el.removeEventListener('wheel', cancel);
      el.removeEventListener('touchstart', cancel);
    };
  }, []);

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last && sentByMe.current.has(String(last.id))) {
      sentByMe.current.delete(String(last.id));
      nearBottomRef.current = true;
      scrollChatToEnd(chatRef, true, autoScrollRef);
      return;
    }
    if (nearBottomRef.current) scrollChatToEnd(chatRef, true, autoScrollRef);
    else setNewBelowCount(c => c + 1);
  }, [messages]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isAdmin) {
      navigate('/');
      return;
    }

    loadChat();

    try {
      const newSocket = io(SOCKET_URL, { auth: { token: user?.token }, timeout: 3000 });
      newSocket.on('connect', () => {
        setConnected(true);
        setUsingLocalStorage(false);
        newSocket.emit('join_conversation', { conversationId: chatId });
      });
      newSocket.on('disconnect', () => setConnected(false));
      newSocket.on('connect_error', () => { setUsingLocalStorage(true); setConnected(false); });
      newSocket.on('conversation_history', (data) => {
        if (data.conversationId === chatId) setMessages(data.messages || []);
      });
      newSocket.on('new_message', (message) => {
        if (message.conversation_id === chatId) setMessages(prev => mergeMessages(prev, [message]));
      });
      const t = setTimeout(() => { if (!newSocket.connected) setUsingLocalStorage(true); }, 3000);
      return () => { clearTimeout(t); newSocket.disconnect(); };
    } catch (err) {
      setUsingLocalStorage(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, isAuthenticated, isAdmin, authLoading, navigate]);

  // Polling localStorage (mode local uniquement)
  useEffect(() => {
    if (!usingLocalStorage) return;
    const checkForNewMessages = () => {
      const chatHistory = JSON.parse(localStorage.getItem('chatHistory') || '[]');
      const chat = chatHistory.find(c =>
        c.id === chatId || c.orderData?.id === chatId || c.orderData?.id?.toString() === chatId
      );
      if (chat?.messages) setMessages(prev => mergeMessages(prev, chat.messages));
    };
    const interval = setInterval(checkForNewMessages, 2000);
    const handleStorageChange = (e) => { if (e.key === 'chatHistory') checkForNewMessages(); };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [usingLocalStorage, chatId]);

  // Polling du serveur Node : messages partagés entre navigateurs (avec fichiers)
  useEffect(() => {
    if (!orderId) return;
    const fetchServerMessages = async () => {
      try {
        const res = await fetch(`${SOCKET_URL}/api/support_messages?orderId=${orderId}`, { headers: authHeaders() });
        const data = await res.json();
        if (data.success && data.messages?.length) {
          const serverMsgs = data.messages.map(m => ({
            id: m.client_id || `srv-${m.id}`,
            sender: m.sender,
            text: m.text,
            content: m.text,
            timestamp: m.client_ts || m.created_at,
            created_at: m.client_ts || m.created_at,
            fileUrl: m.file_url ? `${SOCKET_URL}${m.file_url}` : null,
            fileName: m.file_name,
            fileType: m.file_type,
            status: 'read'
          }));
          setMessages(prev => mergeMessages(prev, serverMsgs));
        }
      } catch (e) { /* serveur indisponible */ }
    };
    fetchServerMessages();
    const interval = setInterval(fetchServerMessages, 2000);
    return () => clearInterval(interval);
  }, [orderId]);

  const loadChat = async () => {
    const chatHistory = JSON.parse(localStorage.getItem('chatHistory') || '[]');
    const chat = chatHistory.find(c => c.id === chatId || c.orderData?.id === chatId || c.orderData?.id?.toString() === chatId);

    if (chat) {
      setChatData(chat);
      setMessages(chat.messages || []);
      setOrderId(chat.orderData?.id || chatId);
      setOrderStatus(chat.orderData?.status || 'pending');
      if (chat.status === 'unread') {
        localStorage.setItem('chatHistory', JSON.stringify(chatHistory.map(c =>
          (c.id === chatId || c.orderData?.id === chatId || c.orderData?.id?.toString() === chatId)
            ? { ...c, status: 'read' } : c
        )));
      }
      setLoading(false);
      return;
    }

    const adminOrders = JSON.parse(localStorage.getItem('admin_orders') || '[]');
    const order = adminOrders.find(o => o.id === chatId || o.id?.toString() === chatId);
    if (order) {
      setChatData({ id: order.id, orderData: order, messages: [] });
      setOrderId(order.id);
      setOrderStatus(order.status || 'pending');
      setLoading(false);
      return;
    }

    // Navigateur différent : récupérer la commande sur le serveur
    try {
      const res = await fetch(`${SOCKET_URL}/api/admin/orders`, { headers: authHeaders() });
      const data = await res.json();
      const srvOrder = (data.orders || []).find(o => o.id === chatId);
      if (srvOrder) {
        setChatData({ id: srvOrder.id, orderData: srvOrder, messages: [] });
        setOrderStatus(srvOrder.status || 'pending');
      }
    } catch (e) { /* serveur indisponible */ }
    setOrderId(chatId);
    setLoading(false);
  };

  const setMsgStatus = (id, status) =>
    setMessages(prev => prev.map(m => m.id === id ? { ...m, status } : m));

  const postToServer = (text, clientId, file = null) =>
    fetch(`${SOCKET_URL}/api/support_messages`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        orderId,
        sender: 'admin',
        text,
        clientId,
        clientTs: new Date().toISOString(),
        ...(file ? { fileData: file.data, fileName: file.name, fileType: file.type } : {})
      })
    }).then(r => r.ok).catch(() => false);

  const retryMessage = (message) => {
    setMsgStatus(message.id, 'sending');
    postToServer(message.text, message.id,
      message.fileData ? { data: message.fileData, name: message.fileName, type: message.fileType } : null
    ).then(ok => setMsgStatus(message.id, ok ? 'sent' : 'error'));
  };

  const saveToLocalHistory = (message) => {
    const chatHistory = JSON.parse(localStorage.getItem('chatHistory') || '[]');
    localStorage.setItem('chatHistory', JSON.stringify(chatHistory.map(c =>
      (c.id === chatId || c.orderData?.id === chatId || c.orderData?.id?.toString() === chatId)
        ? { ...c, messages: [...(c.messages || []), message], status: 'read' }
        : c
    )));
    window.dispatchEvent(new Event('storage'));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text || !orderId) return;

    const clientId = `a-${orderId}-${Date.now()}`;
    const adminMessage = {
      id: clientId,
      sender: 'admin',
      sender_name: 'Support AVKAMAS',
      text,
      timestamp: new Date().toISOString(),
      status: 'sending'
    };
    sentByMe.current.add(clientId);
    setMessages(prev => [...prev, adminMessage]);
    setNewMessage('');

    const ok = await postToServer(text, clientId);
    setMsgStatus(clientId, ok ? 'sent' : 'error');
    if (ok) saveToLocalHistory({ ...adminMessage, status: 'sent' });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!orderId) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      alert(`Fichier trop volumineux (max ${MAX_FILE_MB} Mo)`);
      return;
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      alert('Format non accepté : PNG, JPG, WEBP, GIF ou PDF uniquement.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      const clientId = `a-${orderId}-${Date.now()}`;
      const caption = newMessage.trim();
      const fileMessage = {
        id: clientId,
        sender: 'admin',
        sender_name: 'Support AVKAMAS',
        text: caption,
        fileData: dataUrl,
        fileName: file.name,
        fileType: file.type,
        timestamp: new Date().toISOString(),
        status: 'sending'
      };
      sentByMe.current.add(clientId);
      setMessages(prev => [...prev, fileMessage]);
      setNewMessage('');
      setUploading(true);
      const ok = await postToServer(caption, clientId, { data: dataUrl, name: file.name, type: file.type });
      setUploading(false);
      setMsgStatus(clientId, ok ? 'sent' : 'error');
      if (ok) saveToLocalHistory({ ...fileMessage, fileData: undefined, status: 'sent' });
    };
    reader.readAsDataURL(file);
  };

  // ---- Actions sur la commande (approuver / payer / terminer / annuler) ----
  const requestStatus = (action) => {
    if (action.danger) setConfirmAction(action);
    else doStatusUpdate(action);
  };

  const doStatusUpdate = async (action) => {
    setConfirmAction(null);
    if (!orderId || statusBusy) return;
    setStatusBusy(true);
    try {
      const res = await fetch(`${SOCKET_URL}/api/admin/orders/update_status`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ orderId, status: action.to })
      });
      if (res.ok) {
        setOrderStatus(action.to);
        setChatData(prev => prev ? { ...prev, orderData: { ...prev.orderData, status: action.to } } : prev);

        const statusText = `🔄 Statut de la commande mis à jour : ${STATUS_LABELS[action.to] || action.to}`;
        const clientId = `status-${action.to}-${Date.now()}`;
        const sysMessage = {
          id: clientId,
          sender: 'system',
          text: statusText,
          timestamp: new Date().toISOString(),
          status: 'read'
        };
        sentByMe.current.add(clientId);
        setMessages(prev => [...prev, sysMessage]);
        fetch(`${SOCKET_URL}/api/support_messages`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ orderId, sender: 'system', text: statusText, clientId, clientTs: new Date().toISOString() })
        }).catch(() => {});

        // Miroir localStorage pour le client sur le même navigateur
        const chatHistory = JSON.parse(localStorage.getItem('chatHistory') || '[]');
        localStorage.setItem('chatHistory', JSON.stringify(chatHistory.map(c =>
          c.orderData?.id === orderId
            ? { ...c, messages: [...(c.messages || []), sysMessage], orderData: { ...c.orderData, status: action.to } }
            : c
        )));
        const adminOrders = JSON.parse(localStorage.getItem('admin_orders') || '[]');
        localStorage.setItem('admin_orders', JSON.stringify(
          adminOrders.map(o => o.id === orderId ? { ...o, status: action.to } : o)
        ));
        window.dispatchEvent(new Event('storage'));
      }
    } catch (e) { /* serveur indisponible */ }
    setStatusBusy(false);
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const orderInfo = chatData?.orderData || null;
  const currentStatus = orderStatus || orderInfo?.status || 'pending';
  const actions = ORDER_ACTIONS(currentStatus);

  if (loading) {
    return <CoinLoader fullPage label="Chargement de la conversation..." />;
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-2 h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/admin" className="p-1.5 sm:p-2 -ml-1.5 hover:bg-gray-800 rounded-full flex-shrink-0" aria-label="Retour au dashboard">
                <ArrowLeft size={20} className="text-gray-400" />
              </Link>
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <HeadphonesIcon size={18} className="text-white" />
                </div>
                <div>
                  <h1 className="font-bold">Admin Support</h1>
                  <div className="flex items-center gap-1 text-xs">
                    <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400 animate-pulse' : usingLocalStorage ? 'bg-yellow-400' : 'bg-red-400'}`} />
                    <span className={connected ? 'text-green-400' : usingLocalStorage ? 'text-yellow-400' : 'text-red-400'}>
                      {connected ? 'En ligne' : usingLocalStorage ? 'Mode local' : 'Hors ligne'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors"
            >
              <span className="text-sm">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-6">
        {orderInfo && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-900/80 border border-gray-800 rounded-xl p-3 sm:p-4 mb-3 sm:mb-6"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  orderInfo.type === 'buy' ? 'bg-yellow-500/20' : 'bg-green-500/20'
                }`}>
                  <ShoppingBag size={20} className={orderInfo.type === 'buy' ? 'text-yellow-400' : 'text-green-400'} />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-sm sm:text-base truncate">
                    Commande #{orderInfo.id?.slice(-8) || 'N/A'}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-400 truncate">
                    {orderInfo.type === 'buy' ? 'Achat' : 'Vente'} de {orderInfo.quantity || orderInfo.amount}M Kamas - {orderInfo.server}
                  </p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-yellow-400 text-sm sm:text-base">
                  {parseFloat(orderInfo.totalPrice || orderInfo.total_price || 0).toFixed(2)} DH
                </p>
                <p className="text-xs text-gray-400">{formatDate(orderInfo.created_at || orderInfo.timestamp)}</p>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-800 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className="text-gray-400 text-xs sm:text-sm truncate">{orderInfo.email || 'N/A'}</span>
              </div>
              {orderInfo.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-gray-400 text-xs sm:text-sm">{orderInfo.phone}</span>
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-800 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Statut :</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  currentStatus === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                  currentStatus === 'completed' ? 'bg-green-500/20 text-green-400' :
                  currentStatus === 'paid' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {STATUS_LABELS[currentStatus] || currentStatus}
                </span>
              </div>
              {(orderInfo.character || orderInfo.character_name) && (
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <User size={14} />
                  <span>Personnage : {orderInfo.character || orderInfo.character_name}</span>
                </div>
              )}
            </div>

            {/* Actions sur la commande, directement dans le chat */}
            {actions.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-800 flex flex-wrap gap-2">
                {statusBusy ? (
                  <Loader size={16} className="text-yellow-400 animate-spin" />
                ) : (
                  actions.map(a => (
                    <button
                      key={a.to}
                      onClick={() => requestStatus(a)}
                      className={`px-3 py-1.5 border rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${a.cls}`}
                    >
                      {a.danger ? <XCircle size={13} /> : a.to === 'completed' ? <CheckCircle size={13} /> : <BadgeCheck size={13} />}
                      {a.label}
                    </button>
                  ))
                )}
              </div>
            )}
          </motion.div>
        )}

        <div className="relative">
          <div
            ref={chatRef}
            onScroll={handleScroll}
            className="bg-gray-900/50 border border-gray-800 rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 h-[55vh] min-h-[16rem] sm:h-96 overflow-y-auto"
          >
            <div className="space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">Aucun message pour le moment</p>
                  <p className="text-gray-500 text-xs mt-1">Envoyez le premier message !</p>
                </div>
              ) : (
                messages.map((message) => {
                  const own = message.sender === 'admin' || message.sender_id === user?.id;
                  return (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${own ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] sm:max-w-xs lg:max-w-md break-words ${
                        own
                          ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                          : message.sender === 'system' || message.message_type === 'system'
                          ? 'bg-blue-500/20 border border-blue-500/50 text-blue-400'
                          : 'bg-gray-800 text-white'
                      } rounded-2xl px-3 sm:px-4 py-2 sm:py-3`}>
                        {own && (
                          <div className="flex items-center gap-2 mb-1 pb-2 border-b border-white/20">
                            <HeadphonesIcon size={14} />
                            <span className="text-xs font-medium">Support AVKAMAS</span>
                          </div>
                        )}
                        {(message.sender === 'system' || message.message_type === 'system') && (
                          <div className="flex items-center gap-2 mb-1 pb-2 border-b border-blue-500/30">
                            <Clock size={14} />
                            <span className="text-xs font-medium">Système</span>
                          </div>
                        )}
                        {!own && message.sender === 'user' && (
                          <div className="flex items-center gap-2 mb-1 pb-2 border-b border-gray-700">
                            <User size={14} />
                            <span className="text-xs font-medium">Client</span>
                          </div>
                        )}
                        <Attachment
                          message={message}
                          darkText={own}
                          onPreview={(src, name) => setLightbox({ src, name })}
                        />
                        {(message.text || message.content) && (
                          <p className="text-xs sm:text-sm whitespace-pre-wrap">{message.text || message.content}</p>
                        )}
                        <div className={`flex items-center justify-end gap-1.5 mt-1 text-xs ${
                          own ? 'text-white/70' : 'text-gray-500'
                        }`}>
                          <span>{formatTime(message.timestamp || message.created_at)}</span>
                          {own && <SendStatus status={message.status} onRetry={() => retryMessage(message)} />}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </div>
          <NewMessagesPill
            visible={newBelowCount > 0}
            count={newBelowCount}
            onClick={() => { scrollChatToEnd(chatRef, true, autoScrollRef); setNewBelowCount(0); }}
          />
        </div>

        <form onSubmit={handleSendMessage} className="bg-gray-900/80 border border-gray-800 rounded-xl p-2 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title="Envoyer une image ou un PDF"
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0 disabled:opacity-50"
            >
              {uploading
                ? <Loader size={20} className="text-red-400 animate-spin" />
                : <Paperclip size={20} className="text-gray-400" />}
            </button>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={usingLocalStorage ? 'Réponse (mode local)...' : 'Réponse au client...'}
              maxLength={4000}
              className="flex-1 min-w-0 bg-gray-950 border border-gray-700 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white text-sm focus:border-red-500 focus:outline-none"
            />
            <EmojiButton onPick={(emoji) => setNewMessage(prev => prev + emoji)} />
            <button
              type="submit"
              disabled={!newMessage.trim() || uploading}
              className="p-2 sm:p-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-400 hover:to-red-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              <Send size={18} />
            </button>
          </div>
        </form>

        <div className="mt-3 sm:mt-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-red-400">
            <strong>Information :</strong> Vous répondez en tant qu'administrateur.
            {usingLocalStorage && ' Mode local actif — les messages sont aussi stockés localement.'}
          </p>
        </div>
      </div>

      <ConfirmModal
        open={!!confirmAction}
        danger
        title="Annuler cette commande ?"
        message={confirmAction ? `La commande #${orderId || ''} passera au statut « Annulée ». Le client sera notifié dans le chat.` : ''}
        confirmLabel="Oui, annuler"
        cancelLabel="Retour"
        onConfirm={() => confirmAction && doStatusUpdate(confirmAction)}
        onCancel={() => setConfirmAction(null)}
      />
      <Lightbox src={lightbox?.src} name={lightbox?.name} onClose={() => setLightbox(null)} />
    </div>
  );
}
